import {date, DetailColumns, DetailPane, FormDialog, useDialogs, useErrors} from '@management-ui/core';
import {Edit} from '@mui/icons-material';
import {reverse} from 'named-urls';
import * as React from 'react';
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useRouteMatch} from 'react-router';
import {useHistory} from 'react-router-dom';
import {ServiceContext} from '../../../../components/Services';
import Text from '../../../../components/Text';
import routes from '../../../../routes';
import PackingListForm from '../../forms/PackingListForm';
import Admin from './Admin';
import Tabs from './Tabs';
import Wrapper from './Wrapper';

const PackingList = ({archive = false}) => {
  const services = useContext(ServiceContext);
  const history = useHistory();
  const match = useRouteMatch();

  const {errors, toggleError} = useErrors(
    useMemo(() => ({packingList: 'Sorry the packing list could not be accessed'}), [])
  );

  const {openDialogs, toggleDialog} = useDialogs(['core', 'lock', 'unlock']);

  const idRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState(null);

  const loadList = useCallback((id) => {
    if (idRef.current !== `${id}`) {
      idRef.current = `${id}`;
      setLoading(true);
      services.packingList.getList(id, archive).then(retrieved => {
        setList(retrieved);
        setLoading(false);
      }).catch(() => {
        toggleError('packingList', true);
        setLoading(false);
        setTimeout(() => {
          history.push(archive ? reverse(routes.archive.index, {tab: 'sales-orders'}) : routes.salesOrders.index);
        }, 3000);
      });
    }
  }, [archive, services, history, toggleError]);

  useEffect(() => {
    if (match.params?.listID) {
      loadList(match.params?.listID);
    }
  }, [history, match, list, loadList]);

  const [title, setTitle] = useState('Loading');
  const [crumbs, setCrumbs] = useState([...(archive ? Wrapper.archiveCrumbs : Wrapper.standardCrumbs)]);

  useEffect(() => {
    let header = 'Loading';
    let trail = [];
    if (list) {
      header = list.number;
      trail = [
        ...(list.archived ? Wrapper.archiveCrumbs : Wrapper.standardCrumbs),
        {
          title: list.order.reference,
          link: reverse(list.archived ? routes.archive.salesOrder.detail : routes.salesOrders.detail, {id: list.order.id})
        },
        {
          title: 'Packing Lists',
          link: reverse(list.archived ? routes.archive.salesOrder.tab : routes.salesOrders.tab, {
            id: list.order.id,
            tab: 'packing-lists'
          })
        },
        {title: header, link: ''}
      ];
    }
    setTitle(header);
    setCrumbs(trail);
  }, [list]);

  const handleSaved = useCallback((updated) => {
    toggleDialog('core', false);
    if (updated) {
      setList(updated);
    } else if (list?.id) {
      loadList(list.id);
    }
  }, [toggleDialog, setList, loadList, list]);

  return (
    <Wrapper title={title} order={list} loading={loading} crumbs={crumbs} errors={errors}>
      {list?.id ? (
        <>
          <DetailColumns columns={[

            <DetailPane
              title="Core Details"
              actions={list.archived ? [] : [
                ...(list.locked ? [] : [
                  {title: 'Edit', icon: <Edit/>, onClick: () => toggleDialog('core', true)},
                ])
              ]}
              details={[
                {title: 'Collection Date', value: list.collection_date ? date(list.collection_date) : '-'},
                {title: 'Collection Address', value: <Text text={list.collection_address ?? '-'}/>},
                {
                  title: 'Customer',
                  value: list.customer?.name ?? '-',
                  route: list.customer ? reverse(routes.contacts.detail, {id: list.customer.id}) : null
                },
                {title: 'Customer PO Number', value: list.number ?? '-'},
                {title: 'Dossier Number', value: list.dossier ?? '-'},
                {title: 'Container Number', value: list.container ?? '-'},
                {title: 'Seal Number', value: list.seal ?? '-'},
                {title: 'Currency', value: list.currency}
              ]}
              dialogs={[
                (props) => (
                  <FormDialog
                    {...props}
                    title="Edit Packing List"
                    open={openDialogs['core'] ?? false}
                    onClose={() => toggleDialog('core', false)}
                    render={(props) => (
                      <PackingListForm
                        {...props}
                        list={list}
                        fields="core"
                        onSaved={handleSaved}/>
                    )}
                  />
                ),
              ]}
            />,

            <Admin
              list={list}
              onListUpdated={setList}
              onLoading={setLoading}
            />,
          ]}/>

          <Tabs list={list} onListUpdated={setList}/>
        </>
      ) : null}
    </Wrapper>
  );
};

export default PackingList;
